import React from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  ListSubheader,
  MenuItem,
  Select,
} from "@mui/material";
import { DAY_OPTIONS, dayLabel, normalizeDays } from "./dayTimeFilter";

interface Props {
  /** Currently selected canonical weekday values (lowercase). [] = "All Days". */
  value: string[];
  onChange: (days: string[]) => void;
  width?: number | string | { [k: string]: number | string };
}

/**
 * Multi-select Days filter — visually and behaviourally identical to
 * Subject/Grade multi-selects (checkbox options, menu stays open across
 * selections, Select All / Clear All). Only seven fixed weekdays, so no search
 * box. Inclusive "Match Any" lives in the client filter; this control only owns
 * the selected-day set (canonical lowercase: monday…sunday).
 *
 * Collapsed display: "All Days" (none) · single name · two short names ·
 * "Days · N" (several) — mirrors the Grade/Subject collapse rule.
 */
const DayMultiSelect: React.FC<Props> = ({ value, onChange, width = 160 }) => {
  const renderValue = (selected: string[]) => {
    const days = normalizeDays(selected);
    if (days.length === 0) return "All Days";
    if (days.length === 1) return dayLabel(days[0]);
    const names = days.map(dayLabel);
    if (days.length === 2 && names.join(", ").length <= 20) return names.join(", ");
    return `Days · ${days.length}`;
  };

  const allValues = DAY_OPTIONS.map((d) => d.value);

  return (
    <FormControl size="small" sx={{ width }}>
      <InputLabel shrink>Days</InputLabel>
      <Select
        multiple
        notched
        displayEmpty
        label="Days"
        value={value}
        onChange={(e) => {
          const v = e.target.value;
          onChange(normalizeDays(typeof v === "string" ? v.split(",") : (v as string[])));
        }}
        renderValue={(selected) => renderValue(selected as string[])}
        sx={{ borderRadius: "10px" }}
        MenuProps={{ autoFocus: false, PaperProps: { style: { maxHeight: 360, width: 220 } } }}
      >
        {/* Bulk actions — a non-selectable header so it never enters the value. */}
        <ListSubheader sx={{ bgcolor: "background.paper", px: 1, py: 0.5 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Button size="small" onClick={() => onChange(allValues)} sx={{ textTransform: "none", fontSize: 11.5, minWidth: 0 }}>
              Select All
            </Button>
            <Button size="small" onClick={() => onChange([])} sx={{ textTransform: "none", fontSize: 11.5, minWidth: 0 }} disabled={value.length === 0}>
              Clear All
            </Button>
          </Box>
        </ListSubheader>

        {DAY_OPTIONS.map((d) => (
          <MenuItem key={d.value} value={d.value} sx={{ py: 0.25 }}>
            <Checkbox size="small" checked={value.includes(d.value)} sx={{ py: 0.25, mr: 0.5 }} />
            <ListItemText primary={d.label} primaryTypographyProps={{ fontSize: 13 }} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default DayMultiSelect;
